import { useState } from "react";
import type { AdminAuditEventSummary } from "../../lib/api";
import { consoleApi, type AdminAuditFilters, type NoteEntityType } from "../../lib/admin-console-api";
import { useAppPreferences } from "../../lib/preferences";
import { errorMessage, LoadState, Section, statusLabel, useFormatters, useLoad, type Translate } from "./ui";

const ACTION_LABELS: Record<string, string> = {
  provider_created: "Provider account created",
  provider_owner_created: "Owner account created",
  provider_owner_setup_resent: "Owner setup link sent",
  provider_updated: "Provider profile edited",
  provider_status_updated: "Provider status changed",
  venue_status_updated: "Venue decision",
  venue_taken_down: "Venue taken down",
  venue_restored: "Venue restored",
  ugc_report_resolved: "Report resolved",
  review_status_updated: "Review moderated",
  review_removed: "Published review removed",
  policies_updated: "Platform policies edited",
  promo_code_created: "Promo code created",
  promo_code_updated: "Promo code edited",
  account_deletion_approved: "Account deletion approved",
  account_deletion_rejected: "Account deletion rejected",
  account_deletion_completed: "Account deleted",
  platform_user_invited: "Teammate invited",
  platform_user_access_updated: "Teammate access changed",
  platform_user_invitation_resent: "Teammate invitation resent",
  admin_assistance_started: "Admin started assisting the owner",
  admin_assistance_ended: "Admin stopped assisting",
  admin_assistance_write: "Change made while assisting",
  account_verification_resent: "Verification code sent",
  account_password_reset_sent: "Password reset link sent",
  account_signed_out: "Signed out of all devices",
  account_deactivated: "Account deactivated",
  account_reactivated: "Account reactivated",
  commission_rule_created: "Commission rule added",
  commission_rule_ended: "Commission rule ended",
  settlement_statement_issued: "Settlement statement issued",
  settlement_statement_voided: "Settlement statement voided",
  settlement_payment_recorded: "Settlement payment recorded",
  "provider_settings.business_updated": "Business details edited by the venue",
  "provider_settings.venue_updated": "Venue receipt settings edited",
  "cash_drawer.opened": "Cash drawer opened",
  "cash_drawer.closed": "Cash drawer closed",
  "receipt.issued": "Receipt issued",
  "receipt.printed": "Receipt printed",
  "receipt.voided": "Receipt voided",
  "receipt.payment_recorded": "Receipt payment recorded",
  "receipt.refunded": "Receipt refunded",
  "tournament.created": "Tournament created",
  "tournament.updated": "Tournament edited",
};

export function actionLabel(action: string, t: Translate) {
  return t(ACTION_LABELS[action] ?? action.replaceAll(/[._]/g, " "));
}

function text(value: unknown) {
  return typeof value === "string" && value.trim() ? value : null;
}

/** One short, human sentence of detail for an audit event, when one exists. */
export function eventDetail(event: AdminAuditEventSummary, t: Translate): string | null {
  const before = event.before_json ?? {};
  const after = event.after_json ?? {};
  const fromStatus = text(before.approval_status);
  const toStatus = text(after.approval_status);
  if (fromStatus && toStatus && fromStatus !== toStatus) {
    return t("{from} → {to}", { from: statusLabel(fromStatus, t), to: statusLabel(toStatus, t) });
  }
  if (toStatus && !fromStatus) return statusLabel(toStatus, t);
  if (event.action === "commission_rule_created") {
    return t("{percent}% of {base} from {date}", {
      percent: String(after.percent ?? ""),
      base: baseLabel(String(after.base ?? ""), t),
      date: String(after.effective_from ?? ""),
    });
  }
  if (event.action === "commission_rule_ended") return t("Ends {date}", { date: String(after.effective_to ?? "") });
  if (event.action === "settlement_payment_recorded") {
    return t("{amount} EGP by {method}", { amount: String(after.amount ?? ""), method: String(after.method ?? "") });
  }
  if (event.action === "settlement_statement_issued") {
    return t("{amount} EGP for {start} to {end}", {
      amount: String(after.commission_amount ?? ""),
      start: String(after.period_start ?? ""),
      end: String(after.period_end ?? ""),
    });
  }
  const note = text(after.review_note) ?? text(after.reason) ?? text(after.rationale) ?? text(after.admin_note);
  return note;
}

export function baseLabel(base: string, t: Translate) {
  if (base === "session_time") return t("session time");
  if (base === "food") return t("food and drinks");
  if (base === "total") return t("session time and food");
  return base;
}

export function EventRow({ event, showEntity = false }: { event: AdminAuditEventSummary; showEntity?: boolean }) {
  const { t } = useAppPreferences();
  const { dateTime } = useFormatters();
  const detail = eventDetail(event, t);
  return (
    <li className="console-event">
      <span aria-hidden="true" className="console-event-dot" />
      <div className="min-w-0 flex-1">
        <p className="console-event-title">{actionLabel(event.action, t)}</p>
        {detail ? <p className="console-event-detail">{detail}</p> : null}
        <p className="console-muted">
          {event.actor_name ?? (event.actor_user_id ? t("Staff member") : t("System"))}
          {showEntity ? ` · ${event.entity_type.replaceAll("_", " ")}` : ""}
        </p>
      </div>
      <time className="console-event-time" dateTime={event.created_at}>{dateTime(event.created_at)}</time>
    </li>
  );
}

/** Newest-first history of one record, with "Load older" paging. */
export function ActivityTimeline({ filters, title }: { filters: AdminAuditFilters; title?: string }) {
  const { t } = useAppPreferences();
  const key = JSON.stringify(filters);
  const [older, setOlder] = useState<AdminAuditEventSummary[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState<string | null>(null);
  const page = useLoad(async () => {
    const result = await consoleApi.listAudit({ ...filters, limit: 20 });
    setOlder([]);
    setCursor(result.next_cursor);
    return result.events;
  }, [key]);
  const events = [...(page.data ?? []), ...older];

  const loadMore = async () => {
    if (!cursor) return;
    setLoadingMore(true);
    setMoreError(null);
    try {
      const result = await consoleApi.listAudit({ ...filters, limit: 20, cursor });
      setOlder((current) => [...current, ...result.events]);
      setCursor(result.next_cursor);
    } catch (error) {
      setMoreError(errorMessage(error, t("Older activity could not be loaded.")));
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <Section title={title ?? t("Activity")}>
      <LoadState
        empty={t("No recorded activity yet.")}
        error={page.error}
        isEmpty={events.length === 0}
        loading={page.loading}
        onRetry={() => void page.reload()}
      >
        <ol className="console-timeline">
          {events.map((event) => <EventRow event={event} key={event.id} />)}
        </ol>
        {moreError ? <p className="console-error" role="alert">{moreError}</p> : null}
        {cursor ? (
          <button className="ops-button" data-size="sm" data-variant="secondary" disabled={loadingMore} onClick={() => void loadMore()} type="button">
            {loadingMore ? t("Loading...") : t("Load older activity")}
          </button>
        ) : null}
      </LoadState>
    </Section>
  );
}

export function NotesPanel({ entityType, entityId }: { entityType: NoteEntityType; entityId: string }) {
  const { t } = useAppPreferences();
  const { dateTime } = useFormatters();
  const notes = useLoad(() => consoleApi.listNotes(entityType, entityId).then((result) => result.notes), [entityType, entityId]);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const save = async () => {
    const body = draft.trim();
    if (!body) return;
    setSaving(true);
    setSaveError(null);
    try {
      const created = await consoleApi.addNote(entityType, entityId, body);
      notes.setData((current) => [created, ...(current ?? [])]);
      setDraft("");
    } catch (error) {
      setSaveError(errorMessage(error, t("The note was not saved. Your text is still here; try again.")));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Section description={t("Internal to the gaamed team. Venues and players never see these.")} title={t("Notes")}>
      <form
        className="grid gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <label className="ops-field">
          <span className="ops-label">{t("Add a note")}</span>
          <textarea
            className="ops-textarea"
            maxLength={4000}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={t("What happened, what was agreed, what to follow up on")}
            rows={3}
            value={draft}
          />
        </label>
        {saveError ? <p className="console-error" role="alert">{saveError}</p> : null}
        <div>
          <button className="ops-button" data-size="sm" data-variant="primary" disabled={saving || !draft.trim()} type="submit">
            {saving ? t("Saving...") : t("Save note")}
          </button>
        </div>
      </form>
      <LoadState
        empty={t("No notes yet.")}
        error={notes.error}
        isEmpty={(notes.data ?? []).length === 0}
        loading={notes.loading}
        onRetry={() => void notes.reload()}
      >
        <ul className="console-notes">
          {(notes.data ?? []).map((note) => (
            <li className="console-note" key={note.id}>
              <p className="console-note-body">{note.body}</p>
              <p className="console-muted">
                {note.author_name ?? t("Staff member")} · {dateTime(note.created_at)}
              </p>
            </li>
          ))}
        </ul>
      </LoadState>
    </Section>
  );
}
